// Tipos do serviço `recovery` — Task 2: agregado LegalCase.
// Espelha o contrato do BFF (recovery/src/adapters/lambda/rest_api/app.py).

export const LegalCaseType = {
    EXTRAJUDICIAL_WORKOUT: 'EXTRAJUDICIAL_WORKOUT',
    EXTRAJUDICIAL_COLLECTION: 'EXTRAJUDICIAL_COLLECTION',
    JUDICIAL_EXECUTION: 'JUDICIAL_EXECUTION',
    EXECUTION_DEFENSE: 'EXECUTION_DEFENSE',
    MONITORY: 'MONITORY',
    JUDICIAL_REORGANIZATION: 'JUDICIAL_REORGANIZATION',
    LITIGATION_DEFENSE: 'LITIGATION_DEFENSE',
} as const satisfies Record<string, string>;

export type LegalCaseType = typeof LegalCaseType[keyof typeof LegalCaseType];

export const Pole = {
    ACTIVE: 'ACTIVE',
    PASSIVE: 'PASSIVE',
} as const satisfies Record<string, string>;

export type Pole = typeof Pole[keyof typeof Pole];

export const LegalCaseStatus = {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
} as const satisfies Record<string, string>;

export type LegalCaseStatus = typeof LegalCaseStatus[keyof typeof LegalCaseStatus];

export interface SelectOption {
    value: string;
    label: string;
}

// Opções dos selects vêm do endpoint page-form; estas são o fallback local caso a
// chamada falhe, mantendo o formulário utilizável.
export const CASE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalCaseType.EXTRAJUDICIAL_WORKOUT, label: 'Recuperação extrajudicial' },
    { value: LegalCaseType.EXTRAJUDICIAL_COLLECTION, label: 'Cobrança extrajudicial' },
    { value: LegalCaseType.JUDICIAL_EXECUTION, label: 'Execução judicial' },
    { value: LegalCaseType.EXECUTION_DEFENSE, label: 'Embargos à execução' },
    { value: LegalCaseType.MONITORY, label: 'Ação monitória' },
    { value: LegalCaseType.JUDICIAL_REORGANIZATION, label: 'Recuperação judicial' },
    { value: LegalCaseType.LITIGATION_DEFENSE, label: 'Defesa em contencioso' },
];

export const POLE_OPTIONS: SelectOption[] = [
    { value: Pole.ACTIVE, label: 'Ativo' },
    { value: Pole.PASSIVE, label: 'Passivo' },
];

export const STATUS_OPTIONS: SelectOption[] = [
    { value: LegalCaseStatus.OPEN, label: 'Em curso' },
    { value: LegalCaseStatus.CLOSED, label: 'Encerrado' },
];

export interface LegalCaseListItem {
    id: string;
    caseType: LegalCaseType;
    caseTypeFormatted: string;
    status: LegalCaseStatus;
    statusFormatted: string;
    pole: Pole;
    poleFormatted: string;
    creditorEntity: string;
    externalCaseNumber: string | null;
    court: string | null;
    district: string | null;
    courtDivision: string | null;
    state: string | null;
    claimValue: number | null;
    claimValueFormatted: string | null;
    cpc25Classification: string | null;
    cpc25ClassificationFormatted: string;
    responsibleFirmId: string | null;
    responsibleFirmName: string | null;
    openedAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    // Resumo de acompanhamento (Task 6): o que é filtrável aparece na grid.
    nextDeadlineDate: string | null;
    nextDeadlineOverdue: boolean;
    pendingMeasureCount: number;
    pendingMeasureTypes: string[];
    pendingMeasureTypesFormatted: string[];
}

// Partes e papéis — Task 3.
export const PartyRole = {
    DEBTOR: 'DEBTOR',
    CREDITOR: 'CREDITOR',
    GUARANTOR: 'GUARANTOR',
    COLLATERAL_PROVIDER: 'COLLATERAL_PROVIDER',
    ASSIGNOR: 'ASSIGNOR',
    DRAWEE: 'DRAWEE',
    LAWYER: 'LAWYER',
    JUDICIAL_ADMINISTRATOR: 'JUDICIAL_ADMINISTRATOR',
    COUNTERPARTY: 'COUNTERPARTY',
} as const satisfies Record<string, string>;

export type PartyRole = typeof PartyRole[keyof typeof PartyRole];

// Fallback local dos papéis, caso o page-form não responda (espelha PARTY_ROLE_LABELS do backend).
export const PARTY_ROLE_OPTIONS: SelectOption[] = [
    { value: PartyRole.DEBTOR, label: 'Devedor' },
    { value: PartyRole.CREDITOR, label: 'Credor' },
    { value: PartyRole.GUARANTOR, label: 'Avalista' },
    { value: PartyRole.COLLATERAL_PROVIDER, label: 'Garantidor' },
    { value: PartyRole.ASSIGNOR, label: 'Cedente' },
    { value: PartyRole.DRAWEE, label: 'Sacado' },
    { value: PartyRole.LAWYER, label: 'Advogado' },
    { value: PartyRole.JUDICIAL_ADMINISTRATOR, label: 'Administrador judicial' },
    { value: PartyRole.COUNTERPARTY, label: 'Contraparte' },
];

export interface Party {
    id: string;
    legalCaseId: string;
    role: PartyRole;
    roleFormatted: string;
    personId: string | null;
    name: string | null;
    document: string | null;
    description: string | null;
    createdAt: string | null;
}

// Regra anti-duplicação (ADR 001): com personId, o backend zera name/document (a fonte é o
// person); sem personId, name é obrigatório.
export interface CreatePartyPayload {
    role: PartyRole;
    personId?: string;
    name?: string;
    document?: string;
    description?: string;
}

// Pretensão / Claim — Task 4.
export const BusinessReference = {
    INSTALLMENT: 'INSTALLMENT',
    RECEIVABLE: 'RECEIVABLE',
    GUARANTEE: 'GUARANTEE',
} as const satisfies Record<string, string>;

export type BusinessReference = typeof BusinessReference[keyof typeof BusinessReference];

// Fallback local (espelha BUSINESS_REFERENCE_LABELS do backend).
export const BUSINESS_REFERENCE_OPTIONS: SelectOption[] = [
    { value: BusinessReference.INSTALLMENT, label: 'Parcela' },
    { value: BusinessReference.RECEIVABLE, label: 'Recebível' },
    { value: BusinessReference.GUARANTEE, label: 'Garantia' },
];

export const ObligationType = {
    CPRF: 'CPRF',
    CD: 'CD',
    PJ: 'PJ',
    OPERATION: 'OPERATION',
    TITLE_DEBT: 'TITLE_DEBT',
} as const satisfies Record<string, string>;

export type ObligationType = typeof ObligationType[keyof typeof ObligationType];

// Fallback local (espelha OBLIGATION_TYPE_LABELS do backend).
export const OBLIGATION_TYPE_OPTIONS: SelectOption[] = [
    { value: ObligationType.CPRF, label: 'CPR-F' },
    { value: ObligationType.CD, label: 'Cédula de débito' },
    { value: ObligationType.PJ, label: 'Contrato PJ' },
    { value: ObligationType.OPERATION, label: 'Operação' },
    { value: ObligationType.TITLE_DEBT, label: 'Título (settlement)' },
];

export interface ClaimItem {
    id: string;
    titleNumber: string;
    businessReference: BusinessReference;
    businessReferenceFormatted: string;
    isGuarantee: boolean;
    amountAtFiling: number | null;
    amountAtFilingFormatted: string | null;
    dueDate: string | null;
    paidAmount: number;
    paidAmountFormatted: string | null;
    remainingAmount: number;
    remainingAmountFormatted: string | null;
    obligationType: ObligationType | null;
    obligationTypeFormatted: string;
    externalReference: string | null;
    obligationId: string | null;
    balanceSource: string;
    description: string | null;
    createdAt: string | null;
}

export interface Claim {
    id: string;
    legalCaseId: string;
    principal: number | null;
    principalFormatted: string | null;
    interest: number | null;
    interestFormatted: string | null;
    penalties: number | null;
    penaltiesFormatted: string | null;
    legalCosts: number | null;
    legalCostsFormatted: string | null;
    description: string | null;
    items: ClaimItem[];
    itemsTotal: number;
    itemsTotalFormatted: string | null;
    guaranteeTotal: number;
    guaranteeTotalFormatted: string | null;
    paidTotal: number;
    paidTotalFormatted: string | null;
    remainingTotal: number;
    remainingTotalFormatted: string | null;
    grandTotal: number;
    grandTotalFormatted: string | null;
    createdAt: string | null;
}

export interface CreateClaimPayload {
    principal?: number;
    interest?: number;
    penalties?: number;
    legalCosts?: number;
    description?: string;
}

// Regra anti-duplicação (ADR 002): garantia (GUARANTEE) fica fora do total; o backend
// bloqueia (422) somar INSTALLMENT + GUARANTEE da mesma operação (mesma externalReference).
export interface CreateClaimItemPayload {
    titleNumber: string;
    businessReference: BusinessReference;
    amountAtFiling: number;
    dueDate?: string;
    paidAmount?: number;
    obligationType?: ObligationType;
    externalReference?: string;
    obligationId?: string;
    description?: string;
}

// Andamento processual — Task 5.
export const CaseEventType = {
    FILING: 'FILING',
    PETITION: 'PETITION',
    DECISION: 'DECISION',
    HEARING: 'HEARING',
    CITATION: 'CITATION',
    SEIZURE: 'SEIZURE',
    APPEAL: 'APPEAL',
    SETTLEMENT_EVENT: 'SETTLEMENT_EVENT',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type CaseEventType = typeof CaseEventType[keyof typeof CaseEventType];

// Fallback local (espelha CASE_EVENT_TYPE_LABELS do backend), caso o page-form não responda.
export const CASE_EVENT_TYPE_OPTIONS: SelectOption[] = [
    { value: CaseEventType.FILING, label: 'Distribuição' },
    { value: CaseEventType.PETITION, label: 'Petição' },
    { value: CaseEventType.DECISION, label: 'Decisão' },
    { value: CaseEventType.HEARING, label: 'Audiência' },
    { value: CaseEventType.CITATION, label: 'Citação/Intimação' },
    { value: CaseEventType.SEIZURE, label: 'Penhora' },
    { value: CaseEventType.APPEAL, label: 'Recurso' },
    { value: CaseEventType.SETTLEMENT_EVENT, label: 'Acordo' },
    { value: CaseEventType.OTHER, label: 'Andamento' },
];

export interface CaseEvent {
    id: string;
    legalCaseId: string;
    eventType: CaseEventType;
    eventTypeFormatted: string;
    eventDate: string | null;
    text: string;
    author: string | null;
    sheetReference: string | null;
    createdAt: string | null;
}

// Só o texto é obrigatório. A data do fato é opcional — o backend assume "hoje" quando
// ausente. O autor NÃO vai no payload: o backend o preenche a partir do contexto de
// autenticação (quem registrou).
export interface CreateCaseEventPayload {
    eventType: CaseEventType;
    eventDate?: string;
    text: string;
    sheetReference?: string;
}

// Medidas e prazos — Task 6.
export const LegalMeasureType = {
    SISBAJUD: 'SISBAJUD',
    INFOJUD: 'INFOJUD',
    RENAJUD: 'RENAJUD',
    CERTIFICATE_828_ISSUED: 'CERTIFICATE_828_ISSUED',
    CERTIFICATE_828_REGISTERED: 'CERTIFICATE_828_REGISTERED',
    PROTEST: 'PROTEST',
    ASSET_SEARCH: 'ASSET_SEARCH',
    BOOK_III: 'BOOK_III',
    PRIME: 'PRIME',
    CITATION: 'CITATION',
    DOOR_TO_DOOR_SEIZURE: 'DOOR_TO_DOOR_SEIZURE',
    SUSEP_CNSEG_LETTER: 'SUSEP_CNSEG_LETTER',
} as const satisfies Record<string, string>;

export type LegalMeasureType = typeof LegalMeasureType[keyof typeof LegalMeasureType];

// Fallback local (espelha LEGAL_MEASURE_TYPE_LABELS do backend), caso o page-form não responda.
export const LEGAL_MEASURE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalMeasureType.SISBAJUD, label: 'SISBAJUD' },
    { value: LegalMeasureType.INFOJUD, label: 'INFOJUD' },
    { value: LegalMeasureType.RENAJUD, label: 'RENAJUD' },
    { value: LegalMeasureType.CERTIFICATE_828_ISSUED, label: 'Certidão art. 828 expedida' },
    { value: LegalMeasureType.CERTIFICATE_828_REGISTERED, label: 'Certidão art. 828 registrada' },
    { value: LegalMeasureType.PROTEST, label: 'Protesto' },
    { value: LegalMeasureType.ASSET_SEARCH, label: 'Pesquisa de bens' },
    { value: LegalMeasureType.BOOK_III, label: 'Livro III' },
    { value: LegalMeasureType.PRIME, label: 'PRIME' },
    { value: LegalMeasureType.CITATION, label: 'Citação' },
    { value: LegalMeasureType.DOOR_TO_DOOR_SEIZURE, label: 'Penhora portas a dentro' },
    { value: LegalMeasureType.SUSEP_CNSEG_LETTER, label: 'Ofício SUSEP/CNSEG' },
];

export const LegalMeasureStatus = {
    REQUESTED: 'REQUESTED',
    PENDING: 'PENDING',
    GRANTED: 'GRANTED',
    DENIED: 'DENIED',
    FULFILLED: 'FULFILLED',
    EXPIRED: 'EXPIRED',
} as const satisfies Record<string, string>;

export type LegalMeasureStatus = typeof LegalMeasureStatus[keyof typeof LegalMeasureStatus];

// Fallback local (espelha LEGAL_MEASURE_STATUS_LABELS do backend).
export const LEGAL_MEASURE_STATUS_OPTIONS: SelectOption[] = [
    { value: LegalMeasureStatus.REQUESTED, label: 'Solicitada' },
    { value: LegalMeasureStatus.PENDING, label: 'Pendente' },
    { value: LegalMeasureStatus.GRANTED, label: 'Deferida' },
    { value: LegalMeasureStatus.DENIED, label: 'Indeferida' },
    { value: LegalMeasureStatus.FULFILLED, label: 'Cumprida' },
    { value: LegalMeasureStatus.EXPIRED, label: 'Prescrita' },
];

export const LegalDeadlineType = {
    DEFENSE: 'DEFENSE',
    APPEAL: 'APPEAL',
    COMPLIANCE: 'COMPLIANCE',
    MANIFESTATION: 'MANIFESTATION',
    DOCUMENT_SUBMISSION: 'DOCUMENT_SUBMISSION',
    HEARING: 'HEARING',
    PAYMENT: 'PAYMENT',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type LegalDeadlineType = typeof LegalDeadlineType[keyof typeof LegalDeadlineType];

// Fallback local (espelha LEGAL_DEADLINE_TYPE_LABELS do backend).
export const LEGAL_DEADLINE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalDeadlineType.DEFENSE, label: 'Contestação/Defesa' },
    { value: LegalDeadlineType.APPEAL, label: 'Recurso' },
    { value: LegalDeadlineType.COMPLIANCE, label: 'Cumprimento' },
    { value: LegalDeadlineType.MANIFESTATION, label: 'Manifestação' },
    { value: LegalDeadlineType.DOCUMENT_SUBMISSION, label: 'Juntada de documento' },
    { value: LegalDeadlineType.HEARING, label: 'Audiência' },
    { value: LegalDeadlineType.PAYMENT, label: 'Pagamento' },
    { value: LegalDeadlineType.OTHER, label: 'Outro' },
];

export interface LegalMeasure {
    id: string;
    legalCaseId: string;
    measureType: LegalMeasureType;
    measureTypeFormatted: string;
    status: LegalMeasureStatus;
    statusFormatted: string;
    requestedAt: string | null;
    resolvedAt: string | null;
    description: string | null;
    createdAt: string | null;
}

// status default REQUESTED no backend quando ausente.
export interface CreateLegalMeasurePayload {
    measureType: LegalMeasureType;
    status?: LegalMeasureStatus;
    requestedAt?: string;
    resolvedAt?: string;
    description?: string;
}

export interface LegalDeadline {
    id: string;
    legalCaseId: string;
    deadlineType: LegalDeadlineType;
    deadlineTypeFormatted: string;
    dueDate: string | null;
    description: string | null;
    fulfilled: boolean;
    // Derivado na leitura pelo backend: vencido (dueDate < hoje) e não cumprido.
    overdue: boolean;
    createdAt: string | null;
}

// dueDate é obrigatória (o backend rejeita com 422 se ausente).
export interface CreateLegalDeadlinePayload {
    deadlineType: LegalDeadlineType;
    dueDate: string;
    description?: string;
    fulfilled?: boolean;
}

// Penhora com bem identificado — Task 7.
export const SeizedAssetType = {
    VEHICLE: 'VEHICLE',
    REAL_ESTATE: 'REAL_ESTATE',
    MACHINERY: 'MACHINERY',
    GRAINS: 'GRAINS',
    FINANCIAL: 'FINANCIAL',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type SeizedAssetType = typeof SeizedAssetType[keyof typeof SeizedAssetType];

// Fallback local (espelha SEIZED_ASSET_TYPE_LABELS do backend), caso o page-form não responda.
export const SEIZED_ASSET_TYPE_OPTIONS: SelectOption[] = [
    { value: SeizedAssetType.VEHICLE, label: 'Veículo' },
    { value: SeizedAssetType.REAL_ESTATE, label: 'Imóvel' },
    { value: SeizedAssetType.MACHINERY, label: 'Maquinário' },
    { value: SeizedAssetType.GRAINS, label: 'Grãos' },
    { value: SeizedAssetType.FINANCIAL, label: 'Valores/direitos' },
    { value: SeizedAssetType.OTHER, label: 'Outro' },
];

export const SeizedAssetStatus = {
    LOCATED: 'LOCATED',
    GRANTED: 'GRANTED',
    EFFECTED: 'EFFECTED',
    CIRCULATION_RESTRICTED: 'CIRCULATION_RESTRICTED',
    RESTRICTION_LIFTED: 'RESTRICTION_LIFTED',
    APPREHENDED: 'APPREHENDED',
    APPRAISED: 'APPRAISED',
    AUCTIONED: 'AUCTIONED',
    UNSEIZABLE: 'UNSEIZABLE',
    LIEN_LIFTED: 'LIEN_LIFTED',
} as const satisfies Record<string, string>;

export type SeizedAssetStatus = typeof SeizedAssetStatus[keyof typeof SeizedAssetStatus];

// Fallback local (espelha SEIZED_ASSET_STATUS_LABELS do backend).
export const SEIZED_ASSET_STATUS_OPTIONS: SelectOption[] = [
    { value: SeizedAssetStatus.LOCATED, label: 'Localizado' },
    { value: SeizedAssetStatus.GRANTED, label: 'Penhora deferida' },
    { value: SeizedAssetStatus.EFFECTED, label: 'Penhora efetivada' },
    { value: SeizedAssetStatus.CIRCULATION_RESTRICTED, label: 'Restrição de circulação' },
    { value: SeizedAssetStatus.RESTRICTION_LIFTED, label: 'Baixa da restrição' },
    { value: SeizedAssetStatus.APPREHENDED, label: 'Apreendido' },
    { value: SeizedAssetStatus.APPRAISED, label: 'Avaliado' },
    { value: SeizedAssetStatus.AUCTIONED, label: 'Leiloado' },
    { value: SeizedAssetStatus.UNSEIZABLE, label: 'Impenhorável' },
    { value: SeizedAssetStatus.LIEN_LIFTED, label: 'Penhora levantada' },
];

// Estados terminais do ciclo da penhora (não avançam — o dropdown de status não os oferece
// como origem de nova transição, mas o backend é a autoridade que valida).
export const SEIZED_ASSET_TERMINAL_STATUSES: SeizedAssetStatus[] = [
    SeizedAssetStatus.UNSEIZABLE,
    SeizedAssetStatus.LIEN_LIFTED,
];

export interface SeizedAsset {
    id: string;
    legalCaseId: string;
    assetType: SeizedAssetType;
    assetTypeFormatted: string;
    status: SeizedAssetStatus;
    statusFormatted: string;
    description: string | null;
    collateralAssetUuid: string | null;
    grade: number | null;
    competingCreditor: string | null;
    estimatedValue: number | null;
    estimatedValueFormatted: string | null;
    appraisedValue: number | null;
    appraisedValueFormatted: string | null;
    // identificação por tipo (só a família do assetType vem preenchida)
    plate: string | null;
    renavam: string | null;
    chassis: string | null;
    state: string | null;
    registrationNumber: string | null;
    registryOffice: string | null;
    district: string | null;
    serialNumber: string | null;
    model: string | null;
    harvest: string | null;
    volume: string | null;
    warehouse: string | null;
    createdAt: string | null;
}

// status default LOCATED no backend quando ausente. Os campos de identificação são por
// tipo — só a família do assetType escolhido é enviada.
export interface CreateSeizedAssetPayload {
    assetType: SeizedAssetType;
    status?: SeizedAssetStatus;
    description?: string;
    collateralAssetUuid?: string;
    grade?: number;
    competingCreditor?: string;
    estimatedValue?: number;
    appraisedValue?: number;
    plate?: string;
    renavam?: string;
    chassis?: string;
    state?: string;
    registrationNumber?: string;
    registryOffice?: string;
    district?: string;
    serialNumber?: string;
    model?: string;
    harvest?: string;
    volume?: string;
    warehouse?: string;
}

// Edição parcial de um bem (valor, grau, credor concorrente, identificação). O status muda
// por rota própria (updateSeizedAssetStatus), não por aqui.
export type UpdateSeizedAssetPayload = Partial<Omit<CreateSeizedAssetPayload, 'assetType' | 'status'>>;

// Decisões e audiências — Task 8.
export const CourtDecisionType = {
    RULING: 'RULING',
    APPELLATE_DECISION: 'APPELLATE_DECISION',
    INJUNCTION: 'INJUNCTION',
    RATIFICATION: 'RATIFICATION',
    ORDER: 'ORDER',
    APPEAL_FILED: 'APPEAL_FILED',
} as const satisfies Record<string, string>;

export type CourtDecisionType = typeof CourtDecisionType[keyof typeof CourtDecisionType];

// Fallback local (espelha COURT_DECISION_TYPE_LABELS do backend), caso o page-form não responda.
export const COURT_DECISION_TYPE_OPTIONS: SelectOption[] = [
    { value: CourtDecisionType.RULING, label: 'Sentença' },
    { value: CourtDecisionType.APPELLATE_DECISION, label: 'Acórdão' },
    { value: CourtDecisionType.INJUNCTION, label: 'Liminar' },
    { value: CourtDecisionType.RATIFICATION, label: 'Homologação' },
    { value: CourtDecisionType.ORDER, label: 'Despacho' },
    { value: CourtDecisionType.APPEAL_FILED, label: 'Recurso interposto' },
];

export const HearingOutcome = {
    SCHEDULED: 'SCHEDULED',
    HELD: 'HELD',
    SETTLED: 'SETTLED',
    NOT_SETTLED: 'NOT_SETTLED',
    ADJOURNED: 'ADJOURNED',
    CANCELED: 'CANCELED',
} as const satisfies Record<string, string>;

export type HearingOutcome = typeof HearingOutcome[keyof typeof HearingOutcome];

// Fallback local (espelha HEARING_OUTCOME_LABELS do backend).
export const HEARING_OUTCOME_OPTIONS: SelectOption[] = [
    { value: HearingOutcome.SCHEDULED, label: 'Agendada' },
    { value: HearingOutcome.HELD, label: 'Realizada' },
    { value: HearingOutcome.SETTLED, label: 'Acordo firmado' },
    { value: HearingOutcome.NOT_SETTLED, label: 'Sem acordo' },
    { value: HearingOutcome.ADJOURNED, label: 'Adiada' },
    { value: HearingOutcome.CANCELED, label: 'Cancelada' },
];

export interface CourtDecision {
    id: string;
    legalCaseId: string;
    decisionType: CourtDecisionType;
    decisionTypeFormatted: string;
    decisionDate: string | null;
    summary: string | null;
    // Aponta para a decisão de origem quando esta é um recurso (decisionType APPEAL_FILED).
    relatedDecisionId: string | null;
    createdAt: string | null;
}

// decisionDate é obrigatória (o backend rejeita com 422 se ausente). A decisão não tem ciclo
// de status — é fato congelado; a edição é de campos. `relatedDecisionId` só é enviado quando
// o registro é um recurso que nasce de uma decisão (definido na criação, não editável depois).
export interface CreateCourtDecisionPayload {
    decisionType: CourtDecisionType;
    decisionDate: string;
    summary?: string;
    relatedDecisionId?: string;
}

export type UpdateCourtDecisionPayload = Partial<CreateCourtDecisionPayload>;

export interface Hearing {
    id: string;
    legalCaseId: string;
    scheduledAt: string | null;
    heldAt: string | null;
    // Derivado na leitura pelo backend: realizada quando heldAt está preenchida.
    held: boolean;
    outcome: string | null;
    outcomeStatus: HearingOutcome | null;
    outcomeStatusFormatted: string;
    description: string | null;
    createdAt: string | null;
}

// scheduledAt é obrigatória (o backend rejeita com 422 se ausente). Marcar realizada =
// enviar heldAt + outcome. "Realizada" deriva da presença de heldAt, não há enum de status.
export interface CreateHearingPayload {
    scheduledAt: string;
    heldAt?: string;
    outcome?: string;
    outcomeStatus?: HearingOutcome;
    description?: string;
}

export type UpdateHearingPayload = Partial<CreateHearingPayload>;

// Acordo com versionamento — Task 9.
export const LegalAgreementStatus = {
    IN_FORMALIZATION: 'IN_FORMALIZATION',
    IN_PROGRESS: 'IN_PROGRESS',
    OVERDUE: 'OVERDUE',
    EXECUTED: 'EXECUTED',
    SETTLED: 'SETTLED',
    SUPERSEDED: 'SUPERSEDED',
} as const satisfies Record<string, string>;

export type LegalAgreementStatus = typeof LegalAgreementStatus[keyof typeof LegalAgreementStatus];

// Fallback local (espelha LEGAL_AGREEMENT_STATUS_LABELS do backend), caso o page-form não responda.
export const LEGAL_AGREEMENT_STATUS_OPTIONS: SelectOption[] = [
    { value: LegalAgreementStatus.IN_FORMALIZATION, label: 'Em formalização' },
    { value: LegalAgreementStatus.IN_PROGRESS, label: 'Em cumprimento' },
    { value: LegalAgreementStatus.OVERDUE, label: 'Em atraso' },
    { value: LegalAgreementStatus.EXECUTED, label: 'Executado' },
    { value: LegalAgreementStatus.SETTLED, label: 'Quitado' },
    { value: LegalAgreementStatus.SUPERSEDED, label: 'Substituído' },
];

// Estados terminais do ciclo do acordo (não avançam por mudança de status; o dropdown não os
// oferece como origem de nova transição, mas o backend é a autoridade que valida).
export const LEGAL_AGREEMENT_TERMINAL_STATUSES: LegalAgreementStatus[] = [
    LegalAgreementStatus.EXECUTED,
    LegalAgreementStatus.SETTLED,
    LegalAgreementStatus.SUPERSEDED,
];

// Nota: SUPERSEDED não é opção de mudança de status manual — superseder exige o acordo
// sucessor (operação própria, recoveryApi.supersedeAgreement). O dropdown da seção Acordos
// filtra SUPERSEDED das opções vindas do page-form (ver LegalCaseAgreements.tsx).

export interface LegalAgreement {
    id: string;
    legalCaseId: string;
    status: LegalAgreementStatus;
    statusFormatted: string;
    agreedAmount: number | null;
    agreedAmountFormatted: string | null;
    signedAt: string | null;
    // Aponta para o acordo sucessor quando este foi renegociado (status SUPERSEDED).
    supersededByAgreementId: string | null;
    description: string | null;
    createdAt: string | null;
}

// Sem parcelas — o cronograma é do settlement. O acordo nasce em IN_FORMALIZATION quando o
// status não é informado.
export interface CreateLegalAgreementPayload {
    status?: LegalAgreementStatus;
    agreedAmount?: number;
    signedAt?: string;
    description?: string;
}

// Edição parcial (valor, data, observações). O status muda por rota própria
// (updateAgreementStatus); a superseção é operação própria (supersedeAgreement).
export type UpdateLegalAgreementPayload = Partial<
    Omit<CreateLegalAgreementPayload, 'status'>
>;

// Renegociação: cria o acordo sucessor (com estes dados) e marca o atual como substituído.
export type SupersedeLegalAgreementPayload = Omit<CreateLegalAgreementPayload, 'status'>;

// Depósito e levantamento judicial — Task 10. Sem status: o estado (bloqueado vs. levantado)
// deriva da presença da data de levantamento; o backend devolve `withdrawn` e `statusFormatted`.
export interface JudicialDeposit {
    id: string;
    legalCaseId: string;
    blockedAmount: number | null;
    blockedAmountFormatted: string | null;
    blockedAt: string | null;
    courtRecordsPage: string | null;
    withdrawnAmount: number | null;
    withdrawnAmountFormatted: string | null;
    withdrawnAt: string | null;
    withdrawn: boolean;
    statusFormatted: string;
    description: string | null;
    createdAt: string | null;
}

// Agregado por caso: distingue o total bloqueado do total efetivamente levantado (recuperado
// efetivo), mais o que segue bloqueado sem levantamento.
export interface JudicialDepositSummary {
    totalBlocked: number;
    totalBlockedFormatted: string | null;
    totalWithdrawn: number;
    totalWithdrawnFormatted: string | null;
    blockedNotWithdrawn: number;
    blockedNotWithdrawnFormatted: string | null;
}

// O valor bloqueado é obrigatório (um depósito existe porque algo foi bloqueado). O
// levantamento (valor + data) é opcional e atômico: ou os dois, ou nenhum.
export interface CreateJudicialDepositPayload {
    blockedAmount?: number;
    blockedAt?: string;
    courtRecordsPage?: string;
    withdrawnAmount?: number;
    withdrawnAt?: string;
    description?: string;
}

// Edição parcial (tipicamente registrar o levantamento: withdrawnAmount + withdrawnAt).
export type UpdateJudicialDepositPayload = Partial<CreateJudicialDepositPayload>;

// Beneficiário — Task 11. Registro global (não pende de caso): quem recebe um pagamento do
// jurídico (escritório, tribunal, cartório, ONR/RTD/IEPTB, INPI, avaliador, transportadora).
export interface Beneficiary {
    id: string;
    name: string;
    beneficiaryType: string;
    beneficiaryTypeFormatted: string;
    document: string | null;
    personId: string | null;
    email: string | null;
    phone: string | null;
    mobile: string | null;
    description: string | null;
    createdAt: string | null;
}

export interface CreateBeneficiaryPayload {
    name: string;
    beneficiaryType?: string;
    document?: string;
    personId?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    description?: string;
}

export type UpdateBeneficiaryPayload = Partial<CreateBeneficiaryPayload>;

// Fallback local dos tipos de beneficiário (espelha BENEFICIARY_TYPE_LABELS do backend),
// caso o page-form não responda. Usado no drawer de filtro e no select de tipo.
export const BENEFICIARY_TYPE_OPTIONS: SelectOption[] = [
    { value: 'LAW_FIRM', label: 'Escritório de advocacia' },
    { value: 'COURT', label: 'Tribunal' },
    { value: 'NOTARY', label: 'Tabelião/Cartório' },
    { value: 'ONR', label: 'ONR' },
    { value: 'RTD', label: 'RTD' },
    { value: 'IEPTB', label: 'IEPTB' },
    { value: 'INPI', label: 'INPI' },
    { value: 'APPRAISER', label: 'Avaliador' },
    { value: 'CARRIER', label: 'Transportadora' },
    { value: 'OTHER', label: 'Outro' },
];

// Despesa — Task 11. Raiz própria; `legalCaseId` e `beneficiaryId` são opcionais (há custo sem
// processo e sem beneficiário cadastrado). O backend resolve o nome do beneficiário e o número
// do caso para exibição.
export interface Expense {
    id: string;
    category: string;
    categoryFormatted: string;
    amount: number | null;
    amountFormatted: string | null;
    paidAt: string | null;
    legalCaseId: string | null;
    legalCaseNumber: string | null;
    beneficiaryId: string | null;
    beneficiaryName: string | null;
    description: string | null;
    createdAt: string | null;
}

// Quebra por categoria do agregado de despesas (fecha com o rodapé da planilha do jurídico).
export interface ExpenseCategoryBreakdown {
    category: string;
    categoryFormatted: string;
    count: number;
    total: number;
    totalFormatted: string | null;
}

// Agregado: total dos lançamentos (calculado, nunca digitado) + quebra por categoria.
export interface ExpenseSummary {
    total: number;
    totalFormatted: string | null;
    count: number;
    byCategory: ExpenseCategoryBreakdown[];
}

// O valor é obrigatório; caso e beneficiário são vínculos opcionais.
export interface CreateExpensePayload {
    category: string;
    amount?: number;
    paidAt?: string;
    legalCaseId?: string;
    beneficiaryId?: string;
    description?: string;
}

export type UpdateExpensePayload = Partial<CreateExpensePayload>;

// Fallback local das categorias de despesa (espelha EXPENSE_CATEGORY_LABELS do backend),
// caso o page-form não responda. Usado no drawer de filtro e nos selects.
export const EXPENSE_CATEGORY_OPTIONS: SelectOption[] = [
    { value: 'COURT_COSTS', label: 'Custas judiciais' },
    { value: 'LITIGATION_FEES', label: 'Honorários advocatícios contencioso' },
    { value: 'ADVISORY_FEES', label: 'Honorários advocatícios consultivo' },
    { value: 'REGISTRATION', label: 'Despesas de registro' },
    { value: 'COLLATERAL_MONITORING', label: 'Monitoramento de garantias' },
    { value: 'CREDIT_ACQUISITION_RTD_RI', label: 'Aquisição de créditos para RTD e RI' },
    { value: 'CERTIFICATE_ISSUANCE', label: 'Emissão de certidões' },
    { value: 'NOTARIAL_DEED', label: 'Ata notarial' },
    { value: 'APPRAISAL_TRANSPORT', label: 'Avaliação/transportes de maquinários' },
    { value: 'INPI', label: 'INPI' },
    { value: 'OTHER', label: 'Outro' },
];

export interface LegalCaseDetail extends LegalCaseListItem {
    courtLink: string | null;
    description: string | null;
    parties: Party[];
    claim: Claim | null;
    events: CaseEvent[];
    measures: LegalMeasure[];
    deadlines: LegalDeadline[];
    seizedAssets: SeizedAsset[];
    courtDecisions: CourtDecision[];
    hearings: Hearing[];
    agreements: LegalAgreement[];
    deposits: JudicialDeposit[];
    depositsSummary: JudicialDepositSummary;
    expenses: Expense[];
    expensesSummary: ExpenseSummary;
    responsibleFirmId: string | null;
    responsibleFirm: Beneficiary | null;
    // Nullable: casos antigos (ou um page-view anterior à Task 12) podem não trazer o bloco.
    // Os consumidores (card, edit form, helpers) tratam a ausência.
    risk: LegalCaseRisk | null;
}

// Classificação de risco CPC 25 (Task 12). Opção do page-form traz o percentual sugerido
// embutido (o backend calcula os padrões a partir das planilhas — ADR 003).
export interface RiskClassificationOption extends SelectOption {
    suggestedLossPercent: number;
}

// Fallback local das classificações CPC 25 com o percentual sugerido (Remota 0, Possível 10,
// Provável 80, Não se aplica 0). Usado quando o page-form falha.
export const RISK_CLASSIFICATION_OPTIONS: RiskClassificationOption[] = [
    { value: 'REMOTE', label: 'Remota', suggestedLossPercent: 0 },
    { value: 'POSSIBLE', label: 'Possível', suggestedLossPercent: 10 },
    { value: 'PROBABLE', label: 'Provável', suggestedLossPercent: 80 },
    { value: 'NOT_APPLICABLE', label: 'Não se aplica', suggestedLossPercent: 0 },
];

// Bloco de risco CPC 25 projetado no page-view do caso (todos os valores já calculados no
// backend pela regra do CPC 25 — só passivo provável provisiona).
export interface LegalCaseRisk {
    cpc25Classification: string | null;
    cpc25ClassificationFormatted: string;
    estimatedLossPercent: number | null;
    estimatedLossPercentFormatted: string | null;
    estimatedLossAmount: number | null;
    estimatedLossAmountFormatted: string | null;
    provisionCalculated: number;
    provisionCalculatedFormatted: string | null;
    provisionAmount: number;
    provisionAmountFormatted: string | null;
    provisionOverridden: boolean;
    provisionOverrideAmount: number | null;
    provisionOverrideReason: string | null;
    lossPercentMatchesSuggestion: boolean | null;
}

// Relatório consolidado CPC 25 (GET /legal-cases/cpc25-report) — equivalente calculado da aba
// VALORES da planilha, agrupado por entidade credora × polo × classificação.
export interface Cpc25ReportRow {
    classification: string | null;
    classificationFormatted: string;
    count: number;
    estimatedLoss: number;
    estimatedLossFormatted: string | null;
    provision: number;
    provisionFormatted: string | null;
    offSuggestion: number;
    overridden: number;
}

export interface Cpc25ReportPole {
    pole: string;
    poleFormatted: string;
    rows: Cpc25ReportRow[];
    count: number;
    estimatedLoss: number;
    estimatedLossFormatted: string | null;
    provision: number;
    provisionFormatted: string | null;
    offSuggestion: number;
    overridden: number;
}

export interface Cpc25ReportEntity {
    creditorEntity: string;
    poles: Cpc25ReportPole[];
}

export interface Cpc25ReportSummary {
    count: number;
    estimatedLoss: number;
    estimatedLossFormatted: string | null;
    provision: number;
    provisionFormatted: string | null;
    offSuggestion: number;
    overridden: number;
}

export interface Cpc25AttentionCase {
    id: string;
    externalCaseNumber: string | null;
    creditorEntity: string;
    pole: string;
    poleFormatted: string;
    classification: string | null;
    classificationFormatted: string;
    estimatedLossPercent: number | null;
    estimatedLossPercentFormatted: string | null;
    provisionAmount: number;
    provisionAmountFormatted: string | null;
    provisionOverrideReason: string | null;
    offSuggestion: boolean;
    overridden: boolean;
}

export interface Cpc25Report {
    entities: Cpc25ReportEntity[];
    summary: Cpc25ReportSummary;
    attention: Cpc25AttentionCase[];
}

export interface LegalCaseFormOptions {
    caseTypes: SelectOption[];
    poles: SelectOption[];
    statuses: SelectOption[];
    partyRoles: SelectOption[];
    businessReferences: SelectOption[];
    obligationTypes: SelectOption[];
    caseEventTypes: SelectOption[];
    legalMeasureTypes: SelectOption[];
    legalMeasureStatuses: SelectOption[];
    legalDeadlineTypes: SelectOption[];
    seizedAssetTypes: SelectOption[];
    seizedAssetStatuses: SelectOption[];
    courtDecisionTypes: SelectOption[];
    hearingOutcomes: SelectOption[];
    legalAgreementStatuses: SelectOption[];
    beneficiaryTypes: SelectOption[];
    expenseCategories: SelectOption[];
    riskClassifications: RiskClassificationOption[];
    creditorEntities: SelectOption[];
}

// Entidades credoras do grupo Agrolend (fallback local; a fonte é o page-form). Lista de
// sugestão para o select — o backend aceita texto livre (dado histórico da carga não é recusado).
export const CREDITOR_ENTITY_OPTIONS: SelectOption[] = [
    { value: 'Agrolend Holding', label: 'Agrolend Holding' },
    { value: 'Agrolend SCFI', label: 'Agrolend SCFI' },
    { value: 'Agrolend I FIDC', label: 'Agrolend I FIDC' },
    { value: 'Agrolend II FIAGRO', label: 'Agrolend II FIAGRO' },
];

export interface CreateLegalCasePayload {
    caseType: string;
    pole: string;
    creditorEntity: string;
    status?: string;
    openedAt?: string;
    externalCaseNumber?: string;
    court?: string;
    district?: string;
    courtDivision?: string;
    state?: string;
    courtLink?: string;
    claimValue?: number;
    responsibleFirmId?: string;
    cpc25Classification?: string;
    estimatedLossPercent?: number;
    provisionOverrideAmount?: number;
    provisionOverrideReason?: string;
    description?: string;
}
